import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDownCircle, ArrowUpCircle, Search, SlidersHorizontal } from 'lucide-react';
import Button from '../../components/Button';
import { formatCurrency } from '../../data/mockData';
import { listLots } from '../../api/inventory';
import { useAuth } from '../../context/AuthContext';
import { availableLots } from '../../utils/lots';
import { formatStock, isSoldByWeight } from '../../utils/soldBy';
import form from '../../styles/form.module.css';
import styles from './Inventory.module.css';

const MODE_META = {
  IN: {
    icon: ArrowUpCircle,
    cta: 'Add to stock',
    reasonPlaceholder: 'e.g. Vendor delivery — Amul Dairy',
    intro: 'Scan or search what arrived, enter how much, then set the shelf price.',
  },
  OUT: {
    icon: ArrowDownCircle,
    cta: 'Record stock out',
    reasonPlaceholder: 'e.g. Damaged / order fulfilment',
    intro: 'Pick the product and lot, then enter how much to remove.',
  },
  ADJUST: {
    icon: SlidersHorizontal,
    cta: 'Save adjustment',
    reasonPlaceholder: 'e.g. Recount correction',
    intro: 'Correct the count when the shelf doesn’t match the system.',
  },
};

function productLabel(p) {
  return p ? `${p.name} — ${formatStock(p.stock, p)} in stock` : '';
}

function ProductCombobox({ products, productId, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const product = products.find((p) => p.id === productId);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => (
      p.name.toLowerCase().includes(q)
      || (p.sku || '').toLowerCase().includes(q)
      || (p.barcode || '').toLowerCase().includes(q)
    ));
  }, [products, query]);

  useEffect(() => setHighlight(0), [query, open]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const selectProduct = (p) => {
    onChange(p.id);
    setQuery('');
    setOpen(false);
  };

  const resolveEnterSelection = () => {
    const code = query.trim();
    if (!code) return null;
    const exactBarcode = products.find((p) => p.barcode && p.barcode === code);
    if (exactBarcode) return exactBarcode;
    const exactSku = products.find((p) => (p.sku || '').toLowerCase() === code.toLowerCase());
    if (exactSku) return exactSku;
    if (filtered.length === 1) return filtered[0];
    return filtered[highlight] || null;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      if (!open) setOpen(true);
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      if (!open) return;
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const match = resolveEnterSelection();
      if (match) selectProduct(match);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <div className={styles.comboWrap} ref={wrapRef}>
      <Search size={14} className={styles.comboIcon} />
      <input
        className={`${form.input} ${styles.comboInput}`}
        value={open ? query : productLabel(product)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onKeyDown={handleKeyDown}
        placeholder="Search by name, SKU, or barcode…"
        role="combobox"
        aria-expanded={open}
        aria-controls="stock-move-product-list"
        autoComplete="off"
      />
      {open && (
        <div id="stock-move-product-list" className={styles.comboDropdown} role="listbox">
          {filtered.length === 0 ? (
            <div className={styles.comboEmpty}>
              {products.length === 0 ? 'No products available' : `No products match "${query.trim()}"`}
            </div>
          ) : (
            filtered.map((p, i) => (
              <button
                type="button"
                key={p.id}
                role="option"
                aria-selected={p.id === productId}
                className={`${styles.comboOption} ${i === highlight ? styles.comboOptionActive : ''}`}
                onMouseDown={(e) => { e.preventDefault(); selectProduct(p); }}
                onMouseEnter={() => setHighlight(i)}
              >
                <span className={styles.comboOptionName}>{p.name}</span>
                <span className={styles.comboOptionMeta}>
                  {formatStock(p.stock, p)} in stock
                  {p.barcode ? ` · ${p.barcode}` : ''}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function StockMoveForm({
  mode, products, onSubmit, submitting = false, error = '', initialProductId = '',
}) {
  const { user } = useAuth();
  const [productId, setProductId] = useState(() => (
    initialProductId && products.some((p) => p.id === initialProductId)
      ? initialProductId
      : (products[0]?.id || '')
  ));
  const [qty, setQty] = useState('');
  const [cartons, setCartons] = useState('');
  const [perCarton, setPerCarton] = useState('');
  const [reason, setReason] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [mrp, setMrp] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [cartonCost, setCartonCost] = useState('');
  const [costMode, setCostMode] = useState('unit'); // 'unit' | 'carton'
  const [lotId, setLotId] = useState('');
  const [lots, setLots] = useState([]);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [lotsError, setLotsError] = useState('');
  const [lotsSupported, setLotsSupported] = useState(true);
  const meta = MODE_META[mode];
  const product = products.find((p) => p.id === productId);
  const soldByWeight = isSoldByWeight(product);
  const needsLot = mode !== 'IN' && lotsSupported && lots.length > 0;
  const selectedLot = lots.find((l) => l.lotId === lotId);

  useEffect(() => {
    if (!initialProductId) return;
    if (products.some((p) => p.id === initialProductId)) setProductId(initialProductId);
  }, [initialProductId, products]);

  // Unit products: qty = cartons × items per carton. Weight products: enter kg directly.
  const moveQty = soldByWeight
    ? qty
    : (cartons !== '' && perCarton !== ''
      ? String(Number(cartons) * Number(perCarton))
      : '');

  useEffect(() => {
    if (!productId) return;
    setSellingPrice(product?.price != null ? String(product.price) : '');
    setMrp(product?.mrp != null ? String(product.mrp) : '');
    setUnitCost(product?.costPrice != null ? String(product.costPrice) : '');
    setCartonCost('');
    setCostMode('unit');
    setQty('');
    setCartons('');
    setPerCarton('');
    setLotId('');
    setLots([]);
    setLotsError('');
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!productId || !user?.idToken) return undefined;
    // Stock-in: load lots to show previous shelf prices. Out/Adjust: for lot select.
    let cancelled = false;
    (async () => {
      setLotsLoading(true);
      setLotsError('');
      try {
        const { lots: openLots } = await listLots(productId, user.idToken);
        if (cancelled) return;
        setLotsSupported(true);
        if (mode === 'IN') {
          setLots(openLots);
        } else {
          const selectable = mode === 'OUT' ? availableLots(openLots) : openLots;
          setLots(selectable);
          if (selectable.length === 1) setLotId(selectable[0].lotId);
          else setLotId('');
        }
      } catch (err) {
        if (cancelled) return;
        if (err.status === 404 || err.message === 'not_found') {
          setLotsSupported(false);
          setLots([]);
          setLotId('');
        } else {
          setLotsError(err.message || 'Could not load lots.');
        }
      } finally {
        if (!cancelled) setLotsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, productId, user?.idToken]);

  const previousFieldHint = (catalogValue, lotValues, catalogLabel = 'Catalog') => {
    if (mode !== 'IN' || !product) return '';
    const catalogNum = catalogValue === '' || catalogValue == null ? NaN : Number(catalogValue);
    const uniqueLots = [...new Set(
      lotValues
        .map((v) => (v === '' || v == null ? null : Number(v)))
        .filter((p) => p != null && !Number.isNaN(p)),
    )].sort((a, b) => a - b);
    const parts = [];
    if (!Number.isNaN(catalogNum)) {
      parts.push(`${catalogLabel} ${formatCurrency(catalogNum)}`);
    }
    if (uniqueLots.length === 1) {
      parts.push(`On shelf now ${formatCurrency(uniqueLots[0])}`);
    } else if (uniqueLots.length > 1) {
      parts.push(`On shelf ${uniqueLots.map((p) => formatCurrency(p)).join(' · ')}`);
    }
    return parts.length ? `Was: ${parts.join(' · ')}` : '';
  };

  const previousMrpHint = previousFieldHint(
    product?.mrp,
    lots.map((l) => l.mrp),
    'Catalog',
  );
  const previousCostHint = previousFieldHint(
    product?.costPrice,
    lots.map((l) => l.costPrice),
    'Catalog',
  );

  // Open lots as one-tap price presets (dedupe identical sell/mrp/cost triples).
  const lotPricePresets = useMemo(() => {
    if (mode !== 'IN') return [];
    const byKey = new Map();

    lots.forEach((lot) => {
      const sell = Number(lot.sellingPrice);
      if (!Number.isFinite(sell)) return;
      const mrpVal = lot.mrp == null || lot.mrp === '' ? null : Number(lot.mrp);
      const costVal = lot.costPrice == null || lot.costPrice === '' ? null : Number(lot.costPrice);
      const key = `${sell}|${Number.isFinite(mrpVal) ? mrpVal : ''}|${Number.isFinite(costVal) ? costVal : ''}`;
      const existing = byKey.get(key);
      if (existing) {
        existing.qty += Number(lot.qty) || 0;
        return;
      }
      byKey.set(key, {
        key,
        sellingPrice: sell,
        mrp: Number.isFinite(mrpVal) ? mrpVal : null,
        costPrice: Number.isFinite(costVal) ? costVal : null,
        qty: Number(lot.qty) || 0,
        source: 'lot',
      });
    });

    const presets = [...byKey.values()].sort((a, b) => a.sellingPrice - b.sellingPrice);

    const catalogSell = product?.price == null || product.price === '' ? NaN : Number(product.price);
    if (Number.isFinite(catalogSell) && !presets.some((p) => p.sellingPrice === catalogSell)) {
      const catalogMrp = product?.mrp == null || product.mrp === '' ? null : Number(product.mrp);
      const catalogCost = product?.costPrice == null || product.costPrice === '' ? null : Number(product.costPrice);
      presets.unshift({
        key: `catalog|${catalogSell}`,
        sellingPrice: catalogSell,
        mrp: Number.isFinite(catalogMrp) ? catalogMrp : null,
        costPrice: Number.isFinite(catalogCost) ? catalogCost : null,
        qty: null,
        source: 'catalog',
      });
    }
    return presets;
  }, [mode, lots, product]);

  const applyLotPrice = (preset) => {
    setSellingPrice(String(preset.sellingPrice));
    if (preset.mrp != null && Number.isFinite(preset.mrp)) setMrp(String(preset.mrp));
    if (preset.costPrice != null && Number.isFinite(preset.costPrice)) {
      setCostMode('unit');
      setUnitCost(String(preset.costPrice));
      setCartonCost('');
    }
  };

  const isPresetActive = (preset) => {
    if (sellingPrice === '' || Number(sellingPrice) !== preset.sellingPrice) return false;
    const mrpMatch = preset.mrp == null
      || (mrp !== '' && Number(mrp) === preset.mrp);
    return mrpMatch;
  };

  // Backend always gets per-unit cost. Carton mode: carton cost ÷ items per carton.
  const resolvedUnitCost = (() => {
    if (soldByWeight || costMode === 'unit') {
      return unitCost === '' ? '' : unitCost;
    }
    const carton = Number(cartonCost);
    const pcs = Number(perCarton);
    if (cartonCost === '' || !Number.isFinite(carton) || !Number.isFinite(pcs) || pcs <= 0) return '';
    return String(Math.round((carton / pcs) * 100) / 100);
  })();

  const setCostEntryMode = (next) => {
    if (next === costMode) return;
    if (next === 'carton' && unitCost !== '' && perCarton !== '' && Number(perCarton) > 0) {
      setCartonCost(String(Math.round(Number(unitCost) * Number(perCarton) * 100) / 100));
    } else if (next === 'unit' && resolvedUnitCost !== '') {
      setUnitCost(resolvedUnitCost);
    }
    setCostMode(next);
  };

  const resultingStock = (() => {
    if (!product) return '';
    // When a lot is selected, always base the field on that lot's qty.
    if (selectedLot) {
      const base = Number(selectedLot.qty) || 0;
      if (moveQty === '') return base;
      if (mode === 'OUT') return Math.max(0, base - Math.abs(Number(moveQty) || 0));
      if (mode === 'ADJUST') return Math.max(0, base + Number(moveQty));
      return Math.max(0, base + Math.abs(Number(moveQty) || 0));
    }
    if (moveQty === '') return product.stock ?? '';
    if (mode === 'IN') return Math.max(0, product.stock + Math.abs(Number(moveQty) || 0));
    if (mode === 'OUT') return Math.max(0, product.stock - Math.abs(Number(moveQty) || 0));
    return Math.max(0, product.stock + Number(moveQty));
  })();

  const productStockAfter = (() => {
    if (!product || moveQty === '') return null;
    const delta = mode === 'OUT'
      ? -Math.abs(Number(moveQty) || 0)
      : mode === 'ADJUST'
        ? Number(moveQty) || 0
        : Math.abs(Number(moveQty) || 0);
    return Math.round(Math.max(0, product.stock + delta) * 1000) / 1000;
  })();

  const qtyUnitLabel = soldByWeight ? (product?.unit || 'kg') : 'units';
  const moveQtyDisplay = moveQty === ''
    ? null
    : formatStock(Math.abs(Number(moveQty) || 0), product);
  const submitLabel = (() => {
    if (submitting) return 'Saving…';
    if (mode === 'IN' && moveQtyDisplay) return `Add ${moveQtyDisplay} to stock`;
    if (mode === 'OUT' && moveQtyDisplay) return `Remove ${moveQtyDisplay}`;
    return meta.cta;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!productId || moveQty === '' || Number(moveQty) === 0 || submitting) return;
    if (mode === 'IN' && (sellingPrice === '' || Number(sellingPrice) < 0)) return;
    if (needsLot && !lotId) return;

    const value = mode === 'ADJUST' ? Number(moveQty) : Math.abs(Number(moveQty));
    await onSubmit({
      productId,
      qty: value,
      reason: reason || meta.reasonPlaceholder,
      sellingPrice: mode === 'IN' ? Number(sellingPrice) : undefined,
      mrp: mode === 'IN' && mrp !== '' ? Number(mrp) : undefined,
      unitCost: mode === 'IN' && resolvedUnitCost !== '' ? Number(resolvedUnitCost) : undefined,
      lotId: needsLot ? lotId : (lotId || undefined),
      mode,
    });
    setQty('');
    setCartons('');
    setPerCarton('');
    setCartonCost('');
    setReason('');
  };

  const qtyPreview = (
    <div className={`${styles.qtyPreview} ${moveQty !== '' ? styles.qtyPreviewReady : ''}`} aria-live="polite">
      {moveQty === '' ? (
        <p className={styles.qtyPreviewIdle}>
          {soldByWeight
            ? `Enter ${qtyUnitLabel} to see the new stock total`
            : 'Enter cartons and items per carton to see total units'}
        </p>
      ) : (
        <>
          <div className={styles.qtyPreviewMain}>
            <span className={styles.qtyPreviewDelta}>
              {mode === 'OUT' ? '−' : mode === 'ADJUST' && Number(moveQty) < 0 ? '' : '+'}
              {formatStock(mode === 'ADJUST' ? Number(moveQty) : Math.abs(Number(moveQty) || 0), product)}
            </span>
            <span className={styles.qtyPreviewLabel}>
              {soldByWeight
                ? qtyUnitLabel
                : `${cartons || 0} carton${Number(cartons) === 1 ? '' : 's'} × ${perCarton || 0}`}
            </span>
          </div>
          {product && (
            <div className={styles.qtyPreviewStock}>
              <span>{formatStock(product.stock, product)}</span>
              <span className={styles.qtyPreviewArrow} aria-hidden>→</span>
              <strong>{formatStock(productStockAfter, product)}</strong>
              <span className={styles.qtyPreviewStockLabel}>
                {selectedLot ? 'product total' : 'in stock'}
              </span>
            </div>
          )}
          {selectedLot && (
            <p className={styles.qtyPreviewLot}>
              This lot: {formatStock(selectedLot.qty, product)}
              {' → '}
              {formatStock(resultingStock, product)}
            </p>
          )}
        </>
      )}
    </div>
  );

  const quantityFields = soldByWeight ? (
    <label className={form.field}>
      <span className={form.label}>
        {mode === 'ADJUST' ? 'Adjustment kg (+/−)' : `Weight (${product?.unit || 'kg'})`}
      </span>
      <input
        className={form.input}
        type="number"
        required
        step="0.001"
        value={qty}
        onChange={(e) => setQty(e.target.value)}
        placeholder={mode === 'ADJUST' ? 'e.g. -0.65 or 12.5' : 'e.g. 12.5'}
      />
    </label>
  ) : (
    <div className={form.row}>
      <label className={form.field}>
        <span className={form.label}>
          {mode === 'ADJUST' ? 'Cartons (+/−)' : 'Cartons'}
        </span>
        <input
          className={form.input}
          type="number"
          required
          step="1"
          value={cartons}
          onChange={(e) => setCartons(e.target.value)}
          placeholder={mode === 'ADJUST' ? 'e.g. -2 or 3' : 'e.g. 3'}
        />
      </label>
      <label className={form.field}>
        <span className={form.label}>Items in each carton</span>
        <input
          className={form.input}
          type="number"
          required
          min="1"
          step="1"
          value={perCarton}
          onChange={(e) => setPerCarton(e.target.value)}
          placeholder="e.g. 12"
        />
      </label>
    </div>
  );

  return (
    <div className={`${styles.formCard} ${mode === 'IN' ? styles.formCardWide : ''}`}>
      <form onSubmit={handleSubmit}>
        <p className={styles.formIntro}>{meta.intro}</p>

        <section className={styles.formSection}>
          <p className={styles.formSectionTitle}>
            {mode === 'IN' ? '1. What arrived' : '1. Product'}
          </p>
          <label className={form.field}>
            <span className={form.label}>Product</span>
            <ProductCombobox products={products} productId={productId} onChange={setProductId} />
            {product && (
              <span className={form.hint}>
                Currently {formatStock(product.stock, product)} in stock
                {product.barcode ? ` · Barcode ${product.barcode}` : ''}
              </span>
            )}
          </label>
        </section>

        {mode !== 'IN' && (
          <section className={styles.formSection}>
            <p className={styles.formSectionTitle}>2. Lot</p>
            <label className={form.field}>
              <span className={form.label}>{needsLot ? 'Lot' : 'Lot (optional)'}</span>
              {lotsLoading ? (
                <p className={form.hint}>Loading lots…</p>
              ) : !lotsSupported ? (
                <p className={form.hint}>Lot selection not available yet — move applies to product stock.</p>
              ) : lots.length === 0 ? (
                <p className={form.hint}>No open lots for this product.</p>
              ) : (
                <select
                  className={form.select}
                  required={needsLot}
                  value={lotId}
                  onChange={(e) => setLotId(e.target.value)}
                >
                  <option value="">Select a lot…</option>
                  {lots.map((lot) => (
                    <option key={lot.lotId} value={lot.lotId}>
                      Sell {formatCurrency(lot.sellingPrice)}
                      {lot.mrp != null ? ` · MRP ${formatCurrency(lot.mrp)}` : ''}
                      {' · '}
                      {formatStock(lot.qty, product)} available
                    </option>
                  ))}
                </select>
              )}
              {lotsError && <span className={styles.formError}>{lotsError}</span>}
              {selectedLot && product && (
                <div className={styles.lotQtySummary}>
                  <span>
                    <strong>Total stock</strong>
                    {' '}
                    {formatStock(product.stock, product)}
                  </span>
                  <span className={styles.lotQtySep}>·</span>
                  <span>
                    <strong>This lot</strong>
                    {' '}
                    {formatStock(selectedLot.qty, product)}
                    {selectedLot.sellingPrice != null && (
                      <>
                        {' '}
                        (
                        {formatCurrency(selectedLot.sellingPrice)}
                        )
                      </>
                    )}
                  </span>
                </div>
              )}
            </label>
          </section>
        )}

        <section className={styles.formSection}>
          <p className={styles.formSectionTitle}>
            {mode === 'IN' ? '2. How much arrived' : mode === 'OUT' ? '3. How much to remove' : '3. Correction'}
          </p>
          {quantityFields}
          {qtyPreview}
        </section>

        {mode === 'IN' && (
          <section className={styles.formSection}>
            <p className={styles.formSectionTitle}>3. Shelf price</p>
            <p className={styles.formSectionHint}>
              What customers will pay for this batch. Same price merges into the existing batch.
            </p>

            {lotsLoading ? (
              <p className={form.hint}>Loading existing lot prices…</p>
            ) : lotPricePresets.length > 0 ? (
              <div className={styles.lotPriceBlock}>
                <p className={styles.lotPriceLabel}>Existing prices — tap to reuse</p>
                <div className={styles.lotPriceChips} role="list">
                  {lotPricePresets.map((preset) => {
                    const active = isPresetActive(preset);
                    return (
                      <button
                        key={preset.key}
                        type="button"
                        role="listitem"
                        className={`${styles.lotPriceChip} ${active ? styles.lotPriceChipActive : ''}`}
                        onClick={() => applyLotPrice(preset)}
                        aria-pressed={active}
                      >
                        <span className={styles.lotPriceChipSell}>
                          {formatCurrency(preset.sellingPrice)}
                        </span>
                        <span className={styles.lotPriceChipMeta}>
                          {preset.source === 'catalog'
                            ? 'Catalog default'
                            : (
                              <>
                                {formatStock(preset.qty, product)} on shelf
                                {preset.mrp != null ? ` · MRP ${formatCurrency(preset.mrp)}` : ''}
                              </>
                            )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <div className={form.row}>
              <label className={form.field}>
                <span className={form.label}>
                  {soldByWeight ? `Sell rate / ${product?.unit || 'kg'}` : 'Sell price'}
                </span>
                <div className={form.prefixInput}>
                  <span>₹</span>
                  <input
                    className={form.input}
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={sellingPrice}
                    onChange={(e) => setSellingPrice(e.target.value)}
                    placeholder="e.g. 40"
                  />
                </div>
                <span className={form.hint}>GST-inclusive · or pick a price above</span>
              </label>
              <label className={form.field}>
                <span className={form.label}>MRP (optional)</span>
                <div className={form.prefixInput}>
                  <span>₹</span>
                  <input
                    className={form.input}
                    type="number"
                    min="0"
                    step="0.01"
                    value={mrp}
                    onChange={(e) => setMrp(e.target.value)}
                    placeholder="e.g. 45"
                  />
                </div>
                <span className={form.hint}>
                  {lotsLoading ? 'Loading last MRP…' : previousMrpHint || 'Printed pack MRP'}
                </span>
              </label>
            </div>
            <label className={form.field}>
              <span className={form.label}>Your cost (optional)</span>
              {!soldByWeight && (
                <div className={styles.costModeToggle} role="group" aria-label="Cost entry mode">
                  <button
                    type="button"
                    className={`${styles.costModeBtn} ${costMode === 'unit' ? styles.costModeBtnActive : ''}`}
                    onClick={() => setCostEntryMode('unit')}
                    aria-pressed={costMode === 'unit'}
                  >
                    Unit cost
                  </button>
                  <button
                    type="button"
                    className={`${styles.costModeBtn} ${costMode === 'carton' ? styles.costModeBtnActive : ''}`}
                    onClick={() => setCostEntryMode('carton')}
                    aria-pressed={costMode === 'carton'}
                  >
                    Carton cost
                  </button>
                </div>
              )}
              <div className={form.prefixInput}>
                <span>₹</span>
                <input
                  className={form.input}
                  type="number"
                  min="0"
                  step="0.01"
                  value={soldByWeight || costMode === 'unit' ? unitCost : cartonCost}
                  onChange={(e) => {
                    if (soldByWeight || costMode === 'unit') setUnitCost(e.target.value);
                    else setCartonCost(e.target.value);
                  }}
                  placeholder={
                    soldByWeight || costMode === 'unit'
                      ? 'What you paid per unit'
                      : 'What you paid per carton'
                  }
                />
              </div>
              <span className={form.hint}>
                {lotsLoading
                  ? 'Loading last cost…'
                  : !soldByWeight && costMode === 'carton'
                    ? (
                      perCarton === '' || Number(perCarton) <= 0
                        ? 'Enter items per carton above to convert to unit cost'
                        : resolvedUnitCost !== ''
                          ? `Unit cost ${formatCurrency(Number(resolvedUnitCost))} (${formatCurrency(Number(cartonCost))} ÷ ${perCarton}) · sent to backend`
                          : 'Enter carton cost to calculate unit cost'
                    )
                    : previousCostHint || 'For your records only — not shown on POS'}
              </span>
            </label>
          </section>
        )}

        <section className={styles.formSection}>
          <p className={styles.formSectionTitle}>
            4. Note
            <span className={styles.optionalTag}>optional</span>
          </p>
          <label className={form.field}>
            <span className={form.label}>Reason / reference</span>
            <input
              className={form.input}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={meta.reasonPlaceholder}
            />
          </label>
        </section>

        {error && <p className={styles.formError}>{error}</p>}

        <Button type="submit" icon={meta.icon} className={styles.formSubmit} disabled={submitting || (needsLot && !lotId)}>
          {submitLabel}
        </Button>
      </form>
    </div>
  );
}
